/**
 * A stretch of time with a reason attached, and a clock you can walk away from.
 *
 * Its own module rather than more of screens.js, which is already long enough
 * that finding anything in it is a scroll. Everything here is about one
 * screen and one pill.
 *
 * **The clock is never accumulated.** Every repaint asks `elapsedSeconds` what
 * the session's own record and the wall clock say, so a phone that slept, a
 * tab that was closed and a browser that skipped a hundred frames all come
 * back to the right number instead of to a drifted one. This is the rule the
 * desktop timer arrived at in `44e950c`, for the same reason.
 *
 * **The timer lives on the server.** That is what lets the laptop see it, and
 * what makes closing the tab cost nothing.
 */
import * as ui from './ui.js';
import * as api from './sessions.js';
import { elapsedSeconds, formatClock } from './timer.js';
import { shouldPoll } from './heartbeat.js';
import { onSessionEnded } from './agreements.js';

let source = null;          // { config, session } once signed in
let live = null;            // the running or paused session, as the API sees it
let heart = null;           // the one interval, for the whole signed-in app
let ticks = 0;              // seconds since the heartbeat started
let confirmingFinish = false;
// Called after a session ends, so today's list can redraw with the item now
// ticked. A hook rather than an import: screens.js reaches into this module,
// and importing it back would be a cycle.
let afterEnded = null;

const nowSeconds = () => Date.now() / 1000;

/** What is running right now, as the API last described it. */
export function liveSession() { return live; }

export function setSessionEndedHook(fn) { afterEnded = fn; }

export function setSessionSource(config, session) {
  source = config && session ? { config, session } : null;
  live = null;
  confirmingFinish = false;
  stopHeartbeat();
  paintHomeTimer();
  if (source) startHeartbeat();
}

/**
 * One interval for the whole signed-in app, not one per screen.
 *
 * It used to be started by `openTimer` and cleared on leaving, which is why
 * the pill's clock never moved: nothing repainted it once you were anywhere
 * else. And nothing re-read the session at all, so a timer started on the
 * phone never appeared on a laptop tab that was already open. Both were the
 * same missing thing.
 *
 * Repainting is free and happens every second. Asking the server is not, and
 * happens on the schedule in heartbeat.js -- never while the tab is hidden,
 * and immediately when it stops being.
 */
function startHeartbeat() {
  ticks = 0;
  heart = setInterval(async () => {
    ticks += 1;
    paintHomeTimer();
    if (!ui.el('timer-view').hidden) paintTimer();

    if (shouldPoll({ tick: ticks, live: Boolean(live) && live.state !== 'done', visible: visible() })) {
      await refreshLiveSession();
    }
  }, 1000);

  document.addEventListener('visibilitychange', onVisible);
}

const visible = () => document.visibilityState !== 'hidden';

/** Coming back to the app is the moment its screen is most likely stale. */
function onVisible() {
  if (visible() && source) refreshLiveSession();
}

function stopHeartbeat() {
  if (heart !== null) {
    clearInterval(heart);
    heart = null;
  }
  document.removeEventListener('visibilitychange', onVisible);
}

/** What is running, if anything. Never throws at the caller. */
export async function refreshLiveSession() {
  if (!source) return null;
  try {
    live = await api.currentSession(source.config, source.session);
  } catch {
    // A timer that cannot be read is not worth a red line on the home screen.
    // The pill simply does not appear, and the next open asks again.
    live = null;
  }
  paintHomeTimer();
  return live;
}

/** The pill on the home screen: the clock, from wherever you are. */
export function paintHomeTimer() {
  const pill = ui.el('home-timer');
  const running = Boolean(live) && live.state !== 'done';
  pill.hidden = !running;
  if (!running) return;
  ui.el('home-timer-reason').textContent = live.reason;
  ui.el('home-timer-clock').textContent = formatClock(elapsedSeconds(live, nowSeconds()));
  ui.el('home-timer-state').textContent = live.state === 'paused' ? 'Paused' : 'Running';
}

/* ── the unscheduled session: a name before a clock ─────────── */

export function openUnscheduled() {
  ui.el('unscheduled-form').hidden = false;
  ui.el('unscheduled-error').hidden = true;
  const input = ui.el('unscheduled-input');
  input.value = '';
  // Focused on opening, because the next thing to happen is typing. A field
  // that appears and then waits to be tapped asks for two gestures where one
  // would do.
  input.focus();
}

export function cancelUnscheduled() {
  ui.el('unscheduled-form').hidden = true;
  ui.el('unscheduled-input').value = '';
}

export async function submitUnscheduled(event) {
  event?.preventDefault();
  if (!source) return;

  const input = ui.el('unscheduled-input');
  const reason = input.value.trim();
  if (!reason) {
    // An unnamed stretch of time is a number nobody can act on later, so it
    // is refused rather than saved as "Untitled".
    input.focus();
    return;
  }

  const button = ui.el('unscheduled-start');
  button.disabled = true;
  try {
    live = await api.startSession(source.config, source.session, reason);
    cancelUnscheduled();
    openTimer();
  } catch (error) {
    if (error?.alreadyRunning) {
      // The other device got there first. Showing the session that exists is
      // a better answer than an error about the one that did not start.
      cancelUnscheduled();
      await refreshLiveSession();
      openTimer();
      return;
    }
    const note = ui.el('unscheduled-error');
    note.textContent = error?.message || 'Could not start the timer.';
    note.hidden = false;
  } finally {
    button.disabled = false;
  }
}

/**
 * Start a timer against an assignment, from the agenda.
 *
 * Delegated, because the agenda rows are rebuilt on every Canvas refresh and
 * a listener bound to a row would go with it. The assignment's id and title
 * ride on the element for the same reason.
 *
 * The reason is the assignment's own title rather than something typed: the
 * student already said what they are working on by tapping it.
 */
export async function startForAssignment(event) {
  const button = event.target.closest('.agenda-start');
  if (!button || !source) return;

  const reason = button.dataset.reason || 'Assignment';
  const workItemId = button.dataset.workItem || null;

  button.disabled = true;
  try {
    live = await api.startSession(source.config, source.session, reason, workItemId);
    openTimer();
  } catch (error) {
    if (error?.alreadyRunning) {
      // Something is already running -- possibly on the laptop. Show it
      // rather than refusing, which is the answer that helps.
      await refreshLiveSession();
      openTimer();
      return;
    }
    // The agenda has no error line of its own, and inventing one for this
    // would put a red row in a list of assignments. The pill stays as it was.
  } finally {
    button.disabled = false;
  }
}

/* ── the timer screen ───────────────────────────────────────── */

export function openTimer() {
  ui.showView('timer');
  confirmingFinish = false;
  paintTimer();
}

export function leaveTimer() {
  confirmingFinish = false;
  paintHomeTimer();
  ui.showHome();
}

export function paintTimer() {
  if (!live) return;
  ui.el('timer-clock').textContent = formatClock(elapsedSeconds(live, nowSeconds()));
  ui.el('timer-reason').textContent = live.reason;

  const paused = live.state === 'paused';
  const done = live.state === 'done';
  // "Alter started this" is a different thing to read than "you started
  // this", and the student should not have to work out which.
  const whose = live.origin === 'desktop' ? 'Started by Alter · ' : '';
  ui.el('timer-state').textContent =
    whose + (done ? 'Finished' : paused ? 'Paused' : 'Running');
  ui.el('timer-clock').classList.toggle('is-paused', paused);

  const pause = ui.el('timer-pause');
  pause.hidden = done;
  pause.textContent = paused ? 'Resume' : 'Pause';

  const finish = ui.el('timer-finish');
  finish.hidden = done;
  // Pause, then tap again to confirm you are done. A single tap that ends a
  // session is a session ended by a pocket.
  finish.textContent = confirmingFinish ? 'Tap again to finish' : 'Done';
  finish.classList.toggle('is-confirming', confirmingFinish);
}

export async function toggleTimerPause() {
  if (!source || !live) return;
  confirmingFinish = false;
  const call = live.state === 'paused' ? api.resumeSession : api.pauseSession;
  try {
    live = await call(source.config, source.session, live.sessionId);
  } catch {
    // Leave the screen showing what the server thinks. A pause that did not
    // arrive has not happened, and pretending otherwise loses the minutes.
    await refreshLiveSession();
  }
  paintTimer();
}

export async function finishTimer() {
  if (!source || !live) return;
  if (!confirmingFinish) {
    confirmingFinish = true;
    paintTimer();
    return;
  }
  const ending = live;
  let ended = false;
  try {
    live = await api.endSession(source.config, source.session, live.sessionId, null);
    ended = true;
  } catch {
    await refreshLiveSession();
  }
  confirmingFinish = false;
  paintTimer();
  paintHomeTimer();

  if (ended) {
    // The second of the three agreements: a timer that ends ticks the item it
    // was running against. An unscheduled break was running against nothing,
    // so it ticks nothing -- that is the difference between the two.
    await onSessionEnded(
      { markDone: api.markDone }, source.config, source.session, ending,
    );
    if (afterEnded) afterEnded();
  }
}

/* ── "it was still running yesterday -- when did it end?" ───── */

/**
 * The second half of the forgotten-timer rule.
 *
 * The cap stops the number being absurd; this stops it being silently wrong.
 * Asked once, on the first open that sees it, and dismissing keeps the capped
 * value -- still marked a guess in the record, it just stops asking.
 */
export async function askAboutForgotten() {
  const session = await refreshLiveSession();
  const guessed = Boolean(session) && session.state === 'done' && session.endedAtInferred;
  ui.el('forgotten').hidden = !guessed;
  if (!guessed) return;

  const started = new Date(session.startedAt * 1000);
  ui.el('forgotten-note').textContent =
    `"${session.reason}" was still running from `
    + `${started.toLocaleString([], { weekday: 'long', hour: 'numeric', minute: '2-digit' })}. `
    + 'It stopped counting after four hours. When did you actually finish?';
}

export async function answerForgotten() {
  if (!source || !live) return;
  const value = ui.el('forgotten-time').value;
  if (!value) return;
  // A datetime-local value is read in the browser's own zone, which is the
  // zone the student answered in. Epoch seconds from there, never a string.
  const endedAt = new Date(value).getTime() / 1000;
  if (!Number.isFinite(endedAt)) return;
  try {
    await api.endSession(source.config, source.session, live.sessionId, endedAt);
  } catch {
    // The guess stays and it will ask again, which is the honest outcome.
  }
  dismissForgotten();
}

export function dismissForgotten() {
  ui.el('forgotten').hidden = true;
  live = null;
  paintHomeTimer();
}
